import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, Menu, X } from "lucide-react";
import { NAV } from "../../data/homeContent";

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [workOpen, setWorkOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        // When the mobile menu is open the header must carry its own ground too —
        // otherwise the logo row stays transparent over the hero while the panel
        // below it is solid.
        open ? "bg-[#080B12] py-5" : scrolled ? "bg-[#080B12]/90 backdrop-blur-xl border-b border-[#C9A24B]/15 py-3" : "py-5"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 md:px-8 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="flex items-center">
          <img src="/assets/logos/gme-horizontal-white.webp" alt="Gary Malkin Enterprises" className="h-10 md:h-12 w-auto" />
        </Link>

        <nav className="hidden lg:flex items-center gap-9">
          {NAV.map((item) =>
            item.children ? (
              <div key={item.label} className="relative" onMouseEnter={() => setWorkOpen(true)} onMouseLeave={() => setWorkOpen(false)}>
                <Link
                  to={item.to}
                  data-testid="nav-the-work"
                  onClick={() => setWorkOpen(false)}
                  className="flex items-center gap-1.5 text-[15px] tracking-wide text-cream/85 hover:text-[#E7CC86] transition-colors"
                >
                  {item.label} <ChevronDown size={15} className={`transition-transform ${workOpen ? "rotate-180" : ""}`} />
                </Link>
                <AnimatePresence>
                  {workOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 8 }}
                      className="absolute top-full left-0 pt-4"
                    >
                      <div className="rounded-xl p-2 w-60 bg-[#0B1020] border border-[#C9A24B]/25 shadow-2xl shadow-black/50">
                        {item.children.map((c) => (
                          <Link
                            key={c.label}
                            to={c.to}
                            data-testid={`nav-work-${c.label.toLowerCase().replace(/\s+/g, "-")}`}
                            onClick={() => setWorkOpen(false)}
                            className="block w-full text-left px-4 py-3 rounded-lg text-[15px] text-cream/80 hover:text-[#E7CC86] hover:bg-white/5 transition-colors"
                          >
                            {c.label}
                          </Link>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ) : (
              <Link
                key={item.label}
                to={item.to}
                data-testid={`nav-${item.label.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`}
                className="text-[15px] tracking-wide text-cream/85 hover:text-[#E7CC86] transition-colors"
              >
                {item.label}
              </Link>
            )
          )}
          <button data-testid="nav-connect-btn" onClick={() => navigate("/connect")} className="btn-gold px-6 py-2.5 rounded-full text-sm font-semibold tracking-wide">
            Connect
          </button>
        </nav>

        <button data-testid="nav-mobile-toggle" aria-label={open ? "Close menu" : "Open menu"} aria-expanded={open} className="lg:hidden text-[#E7CC86]" onClick={() => setOpen(!open)}>
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }}
            className="lg:hidden overflow-hidden bg-[#080B12] border-t border-[#C9A24B]/15"
          >
            <div className="px-6 py-6 flex flex-col gap-4">
              {NAV.flatMap((item) =>
                item.children
                  ? [{ ...item, depth: 0 }, ...item.children.map((c) => ({ ...c, depth: 1 }))]
                  : [{ ...item, depth: 0 }]
              ).map((c) => (
                <Link
                  key={c.label}
                  to={c.to}
                  onClick={() => setOpen(false)}
                  className={
                    c.depth
                      ? "text-left text-base text-cream/65 hover:text-[#E7CC86] pl-4 border-l border-[#C9A24B]/25"
                      : "text-left text-lg text-cream/90 hover:text-[#E7CC86]"
                  }
                >
                  {c.label}
                </Link>
              ))}
              <button onClick={() => { setOpen(false); navigate("/connect"); }} className="btn-gold px-6 py-3 rounded-full font-semibold mt-2">Connect</button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
